// src/utils/itemShare.js
// ------------------------------------------------------------
// Share links for a single feed item (event or review).
//
// A shared link is opened by someone else, in a browser that has never run
// this app: no cached feed, no auth, no geolocation permission. So the link
// carries everything needed and the URL — not React state — is the source of
// truth for "which item is open".
//
//   https://travy.buzz/?item=123          → event 123
//   https://travy.buzz/?item=review_45    → review 45
//
// The param stays in the address bar for as long as the overlay is open and is
// stripped when it closes, so a refresh reproduces exactly the same screen.
// This mirrors the ผู้รีวิว share links in reviewerShare.js and deliberately
// reuses their base-url and clipboard helpers (window.location.origin is
// capacitor://localhost inside the native app, which is useless to a friend).
// ------------------------------------------------------------
import { getAppBaseUrl } from './reviewerShare';

export const ITEM_SHARE_PARAM = 'item';

// Ids we can actually resolve: "123" (event) or "review_123" (review).
// Anything else is treated as "no shared item", so a truncated or hand-typed
// URL renders plain Home instead of firing a junk request or leaving an empty
// overlay on screen.
const ITEM_ID_PATTERN = /^(review_)?\d+$/;

/** @param {unknown} id */
export const isShareableItemId = (id) =>
  typeof id === 'string' && ITEM_ID_PATTERN.test(id);

/** raw feed id → absolute share URL (null when the id is unusable). */
export const buildItemShareUrl = (rawId) => {
  const id = rawId === null || rawId === undefined ? '' : String(rawId);
  if (!isShareableItemId(id)) return null;
  return `${getAppBaseUrl()}/?${ITEM_SHARE_PARAM}=${encodeURIComponent(id)}`;
};

/** location.search → raw item id, or null. */
export const parseItemShareParam = (search) => {
  let params;
  try {
    params = new URLSearchParams(search || '');
  } catch (err) {
    return null;
  }
  const raw = params.get(ITEM_SHARE_PARAM);
  return isShareableItemId(raw) ? raw : null;
};

/** location.search minus the item param, ready for navigate(). */
export const searchWithoutItemParam = (search) => {
  let params;
  try {
    params = new URLSearchParams(search || '');
  } catch (err) {
    return '';
  }
  params.delete(ITEM_SHARE_PARAM);
  const next = params.toString();
  return next ? `?${next}` : '';
};

/** Split a raw feed id into the pieces the detail endpoints need. */
export const splitItemId = (rawId) => {
  const id = String(rawId === null || rawId === undefined ? '' : rawId);
  const isReview = id.startsWith('review_');
  return {
    isReview,
    numericId: isReview ? id.slice('review_'.length) : id,
  };
};

export default buildItemShareUrl;
